import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  FileText, 
  Target, 
  Sparkles, 
  Download, 
  CheckCircle, 
  Star, 
  Users, 
  Zap,
  Shield,
  Clock,
  Award,
  TrendingUp,
  Search,
  BarChart3,
  Briefcase,
  GraduationCap,
  Globe,
  Lightbulb,
  MessageSquare,
  Settings,
  BookOpen,
  Layers,
  PenTool,
  FileCheck,
  Brain,
  Rocket,
  Heart,
  MapPin,
  Phone,
  Mail,
  Linkedin,
  ChevronRight
} from "lucide-react";
import { Link } from "react-router-dom";

const SEOContent = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Sparkles,
      title: "AI-Powered Resume Writing",
      description: "Our AI assistant helps you write compelling bullet points, polish your summary, and optimize every section for maximum impact."
    },
    {
      icon: Target,
      title: "Real ATS Score Testing",
      description: "Get instant feedback on how your resume performs against Applicant Tracking Systems. Know your score before you apply."
    },
    {
      icon: FileText,
      title: "50+ Professional Templates",
      description: "Choose from Modern, Classic, Creative, and ATS-Optimized templates designed for every industry and career level."
    },
    {
      icon: Zap,
      title: "Job Description Matching",
      description: "Paste any job description and our AI identifies missing keywords, suggests improvements, and boosts your match rate."
    },
    {
      icon: Download,
      title: "PDF Export & Sharing",
      description: "Download your polished resume as a professional PDF or share directly to WhatsApp, Gmail, and LinkedIn."
    },
    {
      icon: Shield,
      title: "Authenticity Checker",
      description: "Ensure your resume sounds human and authentic. Our AI detector helps you maintain your genuine voice."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Choose Your Template",
      description: "Select from 50+ ATS-friendly templates optimized for your industry. Each template is designed to pass automated screening."
    },
    {
      number: "02",
      title: "Add Your Experience",
      description: "Fill in your details or import your existing resume. Our OCR technology extracts information from PDFs and images automatically."
    },
    {
      number: "03",
      title: "Optimize with AI",
      description: "Let our AI polish your content, suggest powerful action verbs, and optimize keywords for your target job."
    },
    {
      number: "04",
      title: "Download & Apply",
      description: "Export your optimized resume and start applying with confidence. Track your ATS score improvements in real-time."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Software Engineer at Google",
      content: "AI CV WALA helped me optimize my resume for tech roles. The ATS score feature was a game-changer - I went from 45% to 92% match rate!",
      rating: 5
    },
    {
      name: "Michael Rodriguez",
      role: "Marketing Director",
      content: "The AI suggestions were incredibly helpful. It rewrote my bullet points to highlight achievements rather than just listing duties. Got 3x more callbacks.",
      rating: 5
    },
    {
      name: "Emily Thompson",
      role: "Finance Analyst at Goldman Sachs",
      content: "Finally, a resume builder that understands ATS systems. The keyword optimization alone saved me hours of manual work.",
      rating: 5
    }
  ];

  const stats = [
    { value: "500K+", label: "Resumes Created" },
    { value: "92%", label: "Average ATS Score" },
    { value: "3x", label: "More Interview Callbacks" },
    { value: "50+", label: "Professional Templates" }
  ];

  // SEO keyword sections
  const seoSections = [
    {
      id: "best-resume-builder",
      icon: Award,
      title: "Best Resume Builder",
      subtitle: "Create Professional Resumes That Get Results",
      content: "AI CV WALA is the best resume builder for job seekers in 2025. Our platform combines AI-powered writing assistance with professional templates to help you create a resume that stands out. Whether you're a fresh graduate or an experienced professional, our resume builder adapts to your needs with industry-specific suggestions and real-time optimization.",
      features: ["AI-powered content suggestions", "Professional formatting", "Industry-specific templates", "Real-time preview"]
    },
    {
      id: "resume-maker-free",
      icon: FileText,
      title: "Resume Maker Free",
      subtitle: "Build Your Resume Without Spending a Dime",
      content: "Our free resume maker gives you access to premium features without any cost. Create unlimited resumes, access 50+ professional templates, and use our AI writing assistant - all completely free. No credit card required, no hidden fees, no watermarks on your downloads.",
      features: ["100% free to use", "No watermarks", "Unlimited resumes", "Professional PDF export"]
    },
    {
      id: "online-resume-editor",
      icon: PenTool,
      title: "Online Resume Editor",
      subtitle: "Edit Your Resume Anywhere, Anytime",
      content: "Our online resume editor works in any browser, on any device. Make quick edits on your phone, polish your content on your tablet, or do a complete overhaul on your desktop. Your resume is always synced and ready to download whenever you need it.",
      features: ["Cloud-based editing", "Auto-save feature", "Mobile responsive", "Real-time sync"]
    },
    {
      id: "ats-resume-builder",
      icon: Target,
      title: "ATS Resume Builder",
      subtitle: "Beat Applicant Tracking Systems Every Time",
      content: "90% of large companies use ATS to filter resumes before a human ever sees them. Our ATS resume builder ensures your resume passes these automated gatekeepers. We analyze your resume against real ATS algorithms and provide a compatibility score with specific recommendations for improvement. Learn more in our comprehensive guide on how to optimize your resume for ATS.",
      features: ["ATS compatibility testing", "Keyword optimization", "Format compliance", "Score tracking"],
      link: "/blog/ats-resume-optimization"
    },
    {
      id: "ai-resume-writer",
      icon: Brain,
      title: "AI Resume Writer",
      subtitle: "Let AI Help You Tell Your Story",
      content: "Our AI resume writer analyzes your experience and generates powerful, achievement-focused content. It suggests strong action verbs, quantifies your accomplishments, and tailors your resume to specific job descriptions. The result? A resume that sounds professional, authentic, and compelling.",
      features: ["Smart content generation", "Achievement highlighting", "Action verb suggestions", "Personalized recommendations"]
    },
    {
      id: "job-description-matcher",
      icon: Search,
      title: "Job Description Matcher",
      subtitle: "Align Your Resume with Every Application",
      content: "Paste any job description into our matcher and instantly see how your resume stacks up. Our AI identifies the key skills and qualifications employers are looking for, then shows you exactly what's missing from your resume. Optimize for every application in seconds.",
      features: ["Keyword extraction", "Gap analysis", "Match scoring", "Suggestion engine"]
    },
    {
      id: "resume-templates-ats",
      icon: Layers,
      title: "Resume Templates ATS-Safe",
      subtitle: "Beautiful Designs That Pass the Test",
      content: "Choose from 50+ professionally designed templates that are guaranteed to pass ATS screening. Each template is tested against real applicant tracking systems to ensure your resume gets through. Modern, classic, creative, or minimal - find the perfect style for your industry.",
      features: ["50+ ATS-tested templates", "Industry-specific designs", "Easy customization", "Professional layouts"]
    },
    {
      id: "resume-optimization-tool",
      icon: Settings,
      title: "Resume Optimization Tool",
      subtitle: "Fine-Tune Every Element for Maximum Impact",
      content: "Our comprehensive optimization tool analyzes every aspect of your resume. From keyword density to readability scores, from section length to formatting consistency - we provide actionable insights to improve your resume's effectiveness and increase your callback rate.",
      features: ["Comprehensive analysis", "Readability scoring", "Length optimization", "Keyword density check"]
    },
    {
      id: "career-development-platform",
      icon: TrendingUp,
      title: "Career Development Platform",
      subtitle: "Your Complete Job Search Companion",
      content: "AI CV WALA is more than just a resume builder - it's a complete career development platform. Track your job applications, generate cover letters, prepare for interviews, and monitor your progress. Everything you need to land your dream job in one place.",
      features: ["Application tracking", "Cover letter builder", "Interview prep", "Progress monitoring"]
    },
    {
      id: "job-application-helper",
      icon: Briefcase,
      title: "Job Application Helper",
      subtitle: "Streamline Your Entire Application Process",
      content: "From resume creation to follow-up emails, our job application helper guides you through every step. Get email templates for applications, follow-ups, and thank you notes. Track deadlines, set reminders, and never miss an opportunity.",
      features: ["Email templates", "Deadline tracking", "Application reminders", "Follow-up automation"]
    },
    {
      id: "professional-resume-service",
      icon: Sparkles,
      title: "Professional Resume Service",
      subtitle: "Get Professional Results Without Professional Fees",
      content: "Traditional resume writing services charge hundreds of dollars. AI CV WALA gives you the same professional quality for free. Our AI is trained on thousands of successful resumes across industries, giving you expert-level guidance without the expert-level price tag.",
      features: ["Expert-level quality", "Instant results", "No waiting time", "Free professional guidance"]
    },
    {
      id: "resume-quality-checker",
      icon: FileCheck,
      title: "Resume Quality Checker",
      subtitle: "Know Exactly How Strong Your Resume Is",
      content: "Get a comprehensive quality score for your resume with detailed breakdowns across multiple dimensions. Our checker evaluates content, formatting, keyword usage, readability, and more. See exactly where you excel and where you can improve.",
      features: ["Multi-dimensional scoring", "Detailed breakdowns", "Improvement suggestions", "Before/after comparison"]
    },
    {
      id: "interview-preparation-tool",
      icon: MessageSquare,
      title: "Interview Preparation Tool",
      subtitle: "Prepare Confident Answers Based on Your Resume",
      content: "Your resume is just the beginning. Our interview preparation tool generates likely interview questions based on your resume content and target role. Practice your responses, prepare for behavioral questions, and walk into interviews with confidence. Check out our complete interview preparation guide for expert tips.",
      features: ["Custom question generation", "Role-specific prep", "Behavioral questions", "Answer frameworks"],
      link: "/blog/interview-preparation-guide"
    },
    {
      id: "skills-assessment-tool",
      icon: BarChart3,
      title: "Skills Assessment Tool",
      subtitle: "Identify and Showcase Your Strongest Skills",
      content: "Our skills assessment tool helps you identify your most marketable skills and present them effectively. Compare your skills against job requirements, discover skill gaps, and get recommendations for skills to develop or highlight.",
      features: ["Skill gap analysis", "Market relevance scoring", "Development recommendations", "Competency mapping"]
    },
    {
      id: "career-coaching-platform",
      icon: Lightbulb,
      title: "Career Coaching Platform",
      subtitle: "AI-Powered Career Guidance at Your Fingertips",
      content: "Get personalized career advice powered by AI. Whether you're planning your next move, considering a career change, or looking to advance in your current field, our platform provides tailored recommendations based on your experience, skills, and goals.",
      features: ["Personalized advice", "Career path mapping", "Growth recommendations", "Goal tracking"]
    }
  ];

  // Additional SEO content for long-tail keywords
  const industryResumes = [
    { name: "Technology & IT", desc: "Software engineers, data scientists, IT managers" },
    { name: "Healthcare", desc: "Nurses, doctors, medical administrators" },
    { name: "Finance & Banking", desc: "Analysts, accountants, investment professionals" },
    { name: "Marketing & Sales", desc: "Digital marketers, sales managers, brand specialists" },
    { name: "Education", desc: "Teachers, professors, academic administrators" },
    { name: "Engineering", desc: "Mechanical, electrical, civil engineers" },
    { name: "Creative & Design", desc: "Graphic designers, UX/UI, content creators" },
    { name: "Legal", desc: "Lawyers, paralegals, legal assistants" }
  ];

  return (
    <div className="bg-background">
      {/* Stats Section */}
      <section className="py-16 bg-muted/50" aria-labelledby="stats-heading">
        <div className="container mx-auto px-6">
          <h2 id="stats-heading" className="sr-only">AI CV WALA Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-foreground mb-2">{stat.value}</p>
                <p className="text-foreground/70 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Features Section - H2 */}
      <section className="py-20 px-6" aria-labelledby="features-heading">
        <div className="container mx-auto max-w-6xl">
          <header className="text-center mb-16">
            <h2 id="features-heading" className="text-3xl md:text-4xl font-bold mb-4">
              Everything You Need to Land Your Dream Job
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Our AI-powered resume builder combines cutting-edge technology with professional expertise to help you stand out.
            </p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <article 
                key={index} 
                className="p-6 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors"
              >
                <feature.icon className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Keyword Sections - Each with H2 */}
      {seoSections.map((section, index) => (
        <section 
          key={section.id}
          id={section.id}
          className={`py-16 px-6 ${index % 2 === 0 ? 'bg-muted/30' : 'bg-background'}`}
          aria-labelledby={`${section.id}-heading`}
        >
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className={index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}>
                <div className="flex items-center gap-3 mb-4">
                  <section.icon className="w-10 h-10 text-primary" />
                  <span className="text-sm font-medium text-foreground uppercase tracking-wider">Feature</span>
                </div>
                <h2 id={`${section.id}-heading`} className="text-3xl md:text-4xl font-bold mb-3">
                  {section.title}
                </h2>
                <p className="text-lg text-foreground/80 mb-4">{section.subtitle}</p>
                <p className="text-muted-foreground mb-6 leading-relaxed">{section.content}</p>
                <ul className="space-y-3 mb-8">
                  {section.features.map((feat, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/dashboard')} size="lg">
                  Try {section.title} Free
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className={`${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'} bg-card rounded-2xl p-8 border border-border`}>
                <div className="aspect-video bg-secondary/50 rounded-lg flex items-center justify-center">
                  <section.icon className="w-24 h-24 text-primary/30" />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Industry-Specific Resumes Section */}
      <section className="py-20 px-6 bg-secondary/20" aria-labelledby="industries-heading">
        <div className="container mx-auto max-w-6xl">
          <header className="text-center mb-16">
            <h2 id="industries-heading" className="text-3xl md:text-4xl font-bold mb-4">
              Resume Templates for Every Industry
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Our templates are optimized for specific industries with tailored formats, keywords, and layouts.
            </p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industryResumes.map((industry, index) => (
              <article key={index} className="p-6 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors">
                <h3 className="text-lg font-semibold mb-2">{industry.name}</h3>
                <p className="text-sm text-muted-foreground">{industry.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 bg-background" aria-labelledby="how-it-works-heading">
        <div className="container mx-auto max-w-6xl">
          <header className="text-center mb-16">
            <h2 id="how-it-works-heading" className="text-3xl md:text-4xl font-bold mb-4">
              Build Your Professional Resume in 4 Easy Steps
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Create an ATS-optimized resume in under 10 minutes. No design skills required.
            </p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <article key={index} className="relative">
                <span className="text-6xl font-bold text-muted-foreground/30 absolute -top-4 -left-2">
                  {step.number}
                </span>
                <div className="pt-8">
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              onClick={() => navigate('/dashboard')}
              className="px-8 py-6 text-lg"
            >
              Create Your Free Resume Now
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 bg-muted/30" aria-labelledby="testimonials-heading">
        <div className="container mx-auto max-w-6xl">
          <header className="text-center mb-16">
            <h2 id="testimonials-heading" className="text-3xl md:text-4xl font-bold mb-4">
              Trusted by 500,000+ Job Seekers
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              See how AI CV WALA has helped professionals land their dream jobs.
            </p>
          </header>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <article 
                key={index} 
                className="p-6 rounded-xl bg-card border border-border"
              >
                <div className="flex gap-1 mb-4">
                  {Array(testimonial.rating).fill(0).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-primary text-primary" />
                  ))}
                </div>
                <blockquote className="text-muted-foreground mb-4">
                  "{testimonial.content}"
                </blockquote>
                <footer>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                </footer>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6 bg-background" aria-labelledby="use-cases-heading">
        <div className="container mx-auto max-w-6xl">
          <header className="text-center mb-16">
            <h2 id="use-cases-heading" className="text-3xl md:text-4xl font-bold mb-4">
              Perfect for Every Career Stage
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Whether you're a fresh graduate or seasoned executive, we have templates and tools for you.
            </p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: "Recent Graduates", desc: "Entry-level templates that highlight education and skills" },
              { icon: TrendingUp, title: "Career Changers", desc: "Transferable skills focus for smooth transitions" },
              { icon: Award, title: "Senior Professionals", desc: "Executive formats for leadership roles" },
              { icon: Clock, title: "Returning Workers", desc: "Gap-friendly templates for career returners" }
            ].map((item, index) => (
              <article key={index} className="p-6 rounded-xl bg-card border border-border text-center">
                <item.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Guide Section - Long-form SEO Content */}
      <section className="py-20 px-6 bg-secondary/20" aria-labelledby="guide-heading">
        <div className="container mx-auto max-w-4xl">
          <header className="text-center mb-12">
            <h2 id="guide-heading" className="text-3xl md:text-4xl font-bold mb-4">
              The Complete Guide to Building a Winning Resume
            </h2>
            <p className="text-xl text-muted-foreground">
              Expert tips and strategies to make your resume stand out
            </p>
          </header>

          <div className="prose prose-lg max-w-none">
            <article className="bg-card rounded-xl p-8 border border-border mb-8">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-primary" />
                Why ATS Optimization Matters
              </h3>
              <p className="text-muted-foreground mb-4">
                In today's job market, over 90% of Fortune 500 companies use Applicant Tracking Systems (ATS) to filter resumes before they reach human recruiters. This means your resume needs to be optimized not just for human readers, but for these automated screening systems as well.
              </p>
              <p className="text-muted-foreground mb-4">
                An ATS scans your resume for specific keywords, formatting patterns, and structure. If your resume doesn't match the system's criteria, it may never be seen by a hiring manager - regardless of how qualified you are for the position.
              </p>
              <p className="text-muted-foreground">
                AI CV WALA's ATS optimization ensures your resume passes these automated gatekeepers by analyzing keyword usage, formatting compliance, and section structure against real ATS algorithms.
              </p>
            </article>

            <article className="bg-card rounded-xl p-8 border border-border mb-8">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Rocket className="w-6 h-6 text-primary" />
                How AI Improves Your Resume
              </h3>
              <p className="text-muted-foreground mb-4">
                Our AI resume writer uses advanced natural language processing to analyze your experience and generate compelling, achievement-focused content. Unlike generic resume builders, our AI understands the nuances of different industries and tailors its suggestions accordingly.
              </p>
              <ul className="space-y-3 text-muted-foreground mb-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span><strong>Action Verbs:</strong> Transform passive descriptions into dynamic, achievement-focused statements</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span><strong>Quantification:</strong> Add metrics and numbers to demonstrate concrete impact</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span><strong>Keyword Optimization:</strong> Naturally incorporate industry-relevant terms</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span><strong>Authenticity:</strong> Maintain your unique voice while enhancing professionalism</span>
                </li>
              </ul>
            </article>

            <article className="bg-card rounded-xl p-8 border border-border">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Heart className="w-6 h-6 text-primary" />
                Best Practices for Resume Success
              </h3>
              <div className="space-y-4 text-muted-foreground">
                <p><strong>1. Tailor for Each Application:</strong> Customize your resume for each job by incorporating keywords from the job description. Our job description matcher makes this easy.</p>
                <p><strong>2. Focus on Achievements:</strong> Instead of listing duties, highlight specific accomplishments with measurable results.</p>
                <p><strong>3. Keep It Concise:</strong> One page for entry-level, two pages maximum for experienced professionals.</p>
                <p><strong>4. Use Clean Formatting:</strong> ATS-friendly templates ensure your content is parsed correctly.</p>
                <p><strong>5. Proofread Carefully:</strong> Use our grammar checker to catch errors before submitting.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* FAQ Section with Schema Markup */}
      <section className="py-20 px-6 bg-background" aria-labelledby="faq-heading">
        <div className="container mx-auto max-w-4xl">
          <header className="text-center mb-16">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground">Everything you need to know about AI CV WALA</p>
          </header>

          <div className="space-y-6">
            {[
              {
                q: "Is AI CV WALA resume builder completely free?",
                a: "Yes! AI CV WALA offers a completely free resume builder with AI assistance, ATS optimization, and access to 50+ professional templates. No credit card required to get started."
              },
              {
                q: "What is ATS and why does it matter for my resume?",
                a: "ATS (Applicant Tracking System) is software used by 90% of companies to automatically screen resumes before human review. Our ATS optimizer ensures your resume passes these automated filters and reaches hiring managers."
              },
              {
                q: "How does the AI resume writer help me?",
                a: "Our AI analyzes your experience and target job descriptions to suggest powerful bullet points, optimize keywords, and improve your resume's impact. It enhances your content while maintaining your authentic voice."
              },
              {
                q: "Can I import my existing resume?",
                a: "Absolutely! AI CV WALA supports OCR import from PDF and image files (PNG, JPG, WEBP). Simply upload your existing resume and our AI will extract and organize your information automatically."
              },
              {
                q: "How many resume templates are available?",
                a: "We offer 50+ professional resume templates across four categories: Modern, Classic, Creative, and ATS-Optimized. Each template is designed by HR experts and tested against real ATS systems."
              },
              {
                q: "Can I download my resume as PDF?",
                a: "Yes, you can download your completed resume as a professional PDF file, optimized for both digital submission and printing. You can also share directly to WhatsApp or Gmail."
              },
              {
                q: "What makes AI CV WALA different from other resume builders?",
                a: "AI CV WALA combines AI-powered writing assistance with real ATS testing. Unlike other builders, we provide actual ATS compatibility scores and specific recommendations to improve your resume's chances of passing automated screening."
              },
              {
                q: "Is my data secure?",
                a: "Yes, we take data security seriously. Your resume data is encrypted and stored securely. We never share your personal information with third parties without your consent."
              },
              {
                q: "Can I create multiple versions of my resume?",
                a: "Yes! You can create unlimited resume versions tailored to different job applications. Our version history feature lets you track changes and restore previous versions."
              },
              {
                q: "How do I optimize my resume for a specific job?",
                a: "Simply paste the job description into our Job Description Matcher. Our AI will analyze the requirements, identify key skills and keywords, and show you exactly what to add or modify in your resume for the best match."
              }
            ].map((faq, index) => (
              <article 
                key={index}
                className="p-6 rounded-lg bg-card border border-border"
              >
                <h3 className="text-lg font-semibold mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Links Section */}
      <section className="py-16 px-6 bg-muted/30" aria-labelledby="resources-heading">
        <div className="container mx-auto max-w-6xl">
          <header className="text-center mb-12">
            <h2 id="resources-heading" className="text-3xl md:text-4xl font-bold mb-4">
              Resume Writing Resources
            </h2>
            <p className="text-muted-foreground">Expert guides and tips to help you succeed</p>
          </header>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/blog/ats-friendly-resume-format" className="block p-6 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors">
              <BookOpen className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">ATS-Friendly Resume Format Guide</h3>
              <p className="text-sm text-muted-foreground">Learn how to format your resume to pass ATS screening every time.</p>
            </Link>
            <Link to="/blog/resume-examples" className="block p-6 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors">
              <FileText className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Resume Examples by Industry</h3>
              <p className="text-sm text-muted-foreground">See real resume examples for 20+ industries and career levels.</p>
            </Link>
            <Link to="/blog/how-to-write-resume-2026" className="block p-6 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors">
              <PenTool className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">How to Write a Resume in 2026</h3>
              <p className="text-sm text-muted-foreground">The complete guide to modern resume writing with AI tools.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 bg-primary text-primary-foreground" aria-labelledby="cta-heading">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 id="cta-heading" className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Land More Interviews?
          </h2>
          <p className="text-xl text-primary-foreground mb-8 max-w-2xl mx-auto">
            Join 500,000+ job seekers who've created ATS-optimized resumes with AI CV WALA. Start building your professional resume today - it's free!
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            onClick={() => navigate('/dashboard')}
            className="px-8 py-6 text-lg"
          >
            Create Your Free Resume Now
          </Button>
          <p className="mt-4 text-sm text-primary-foreground/80">
            No credit card required • 50+ templates • AI-powered
          </p>
        </div>
      </section>

      {/* Breadcrumb Navigation (Hidden visually but SEO-friendly) */}
      <nav aria-label="Breadcrumb" className="sr-only">
        <ol itemScope itemType="https://schema.org/BreadcrumbList">
          <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <a itemProp="item" href="https://aicvwala.com">
              <span itemProp="name">Home</span>
            </a>
            <meta itemProp="position" content="1" />
          </li>
        </ol>
      </nav>
    </div>
  );
};

export default SEOContent;
